/**
 * AI-detected field boundaries shared by every GIS surface that shows them as an
 * optional overlay — see [`docs/GIS-DETECTED-FIELDS.md`](../../../docs/GIS-DETECTED-FIELDS.md).
 *
 * Map-agnostic on purpose: the Leaflet GIS map and the OpenLayers request-form map
 * draw the same data, so only geometry and popup text live here.
 */
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  AiFieldBoundaryDto,
  AiFieldBoundaryService,
  GetBoundariesByBboxRequest,
  MapBoundariesResponse,
} from '@6grain/masofa-monolith-api-client/ai-inference';
import { escapePopupHtml } from '@app/pages/electronic-field-map/pages/gis-fields/managers/gis-popup-html.util';

/** Stroke and fill of detected contours, kept identical to the field-identification map. */
export const DETECTED_FIELDS_COLOR = '#26bd6c';
export const DETECTED_FIELDS_FILL_OPACITY = 0.4;
/** Same fill for map APIs that take one CSS colour instead of colour + opacity (OpenLayers). */
export const DETECTED_FIELDS_FILL_RGBA = 'rgba(38, 189, 108, 0.4)';
export const DETECTED_FIELDS_DASH_ARRAY = '4, 4';
export const DETECTED_FIELDS_DASH_PATTERN = [4, 4];
export const DETECTED_FIELDS_STROKE_WIDTH = 2;
/** Identification draws with Leaflet's opaque default; the shared GIS controller would dim it to 0.8. */
export const DETECTED_FIELDS_STROKE_OPACITY = 1;

export interface DetectedFieldsViewport {
  west: number;
  south: number;
  east: number;
  north: number;
  zoom: number;
}

export interface DetectedFieldsResult {
  boundaries: AiFieldBoundaryDto[];
  /** Server answered with region clusters: the viewport is too coarse for boundaries. */
  zoomTooCoarse: boolean;
}

@Injectable({ providedIn: 'root' })
export class DetectedFieldsService {
  private readonly aiFieldBoundaryService = inject(AiFieldBoundaryService);
  private readonly translate = inject(TranslateService);

  loadBoundaries(viewport: DetectedFieldsViewport): Observable<DetectedFieldsResult> {
    const request: GetBoundariesByBboxRequest = {
      minLon: viewport.west,
      minLat: viewport.south,
      maxLon: viewport.east,
      maxLat: viewport.north,
      zoom: viewport.zoom,
    };

    return this.aiFieldBoundaryService.aiInferenceAiFieldBoundaryGetBoundariesByBboxPost(request).pipe(
      map((response: MapBoundariesResponse) => ({
        boundaries: response.aiBoundaries ?? [],
        zoomTooCoarse: !response.aiBoundaries?.length && !!response.clusters?.length,
      })),
    );
  }

  /** Cadastral number, area and perimeter as escaped HTML for a map popup / tooltip. */
  buildPopupHtml(boundary: AiFieldBoundaryDto): string {
    const title = boundary.cadastralNumber
      ? escapePopupHtml(boundary.cadastralNumber)
      : escapePopupHtml(this.translate.instant('Detected Fields'));
    const areaHa = this.formatNumber(boundary.areaHa, 2);
    const area = areaHa ? `${areaHa} ${escapePopupHtml(this.translate.instant('HA'))}` : '—';
    const perimeter = this.formatNumber(boundary.perimeterM, 0) || '—';

    return (
      `<b>${title}</b>` +
      `<br><small>${escapePopupHtml(this.translate.instant('Area'))}: ${area}</small>` +
      `<br><small>${escapePopupHtml(this.translate.instant('Perimeter (m)'))}: ${perimeter}</small>`
    );
  }

  /**
   * GeoJSON geometry → one outer ring per polygon part as `[lat, lng][]`.
   * MultiPolygon parts stay separate; merging them into one ring set would render
   * every part after the first as a hole.
   */
  geometryToRings(geometry: unknown): Array<Array<[number, number]>> {
    const parsed = typeof geometry === 'string' ? this.parseGeometryJson(geometry) : geometry;
    const geoJson = parsed as { type?: string; coordinates?: number[][][] | number[][][][] } | null;
    if (!geoJson?.coordinates) {
      return [];
    }

    const parts: number[][][][] =
      geoJson.type === 'MultiPolygon'
        ? (geoJson.coordinates as number[][][][])
        : [geoJson.coordinates as number[][][]];

    return parts
      .map((rings) => this.ringToLatLng(rings?.[0] ?? []))
      .filter((ring) => ring.length >= 3);
  }

  private parseGeometryJson(geometry: string): unknown {
    try {
      return JSON.parse(geometry);
    } catch {
      return null;
    }
  }

  private ringToLatLng(ring: number[][]): Array<[number, number]> {
    return ring
      .filter((point) => Number.isFinite(point?.[0]) && Number.isFinite(point?.[1]))
      .map((point) => [point[1], point[0]] as [number, number]);
  }

  private formatNumber(value: number | null | undefined, digits: number): string {
    return value != null && Number.isFinite(Number(value)) ? Number(value).toFixed(digits) : '';
  }
}
